import React, { forwardRef, useCallback, useMemo, useRef } from 'react';
import type { ScrollView } from 'react-native';
import type { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import Animated from 'react-native-reanimated';

type AnimatedScrollViewProps = Omit<React.ComponentProps<typeof Animated.ScrollView>, 'ref'>;

type KeyboardAwareScrollViewComponent = NonNullable<
  React.ComponentProps<typeof KeyboardAwareScrollView>['ScrollViewComponent']
>;

/**
 * Wires up the scroll view both survey screens hand to
 * `KeyboardAwareScrollView`, and gives them a way to scroll it themselves.
 *
 * Two things need a handle on the same scroll view and neither can be dropped:
 *
 * The library scrolls the focused input clear of the keyboard, and to do that
 * it passes its own `ref` down to whatever component it is told to render. It
 * is a plain prop under React 19, so a component that sets `ref` after
 * spreading its props silently discards it — the library keeps working, minus
 * the one behavior it exists for, and the respondent types behind the
 * keyboard. Both refs are therefore called here rather than one winning.
 *
 * The screens scroll to the top on page changes and on a blocked submit, which
 * needs a ref the library cannot give them: its own `useImperativeHandle`
 * shallow-spreads the scroll view instance, so the returned object carries the
 * own properties and none of the prototype methods. `scrollTo` is a prototype
 * method. The call still type-checks, because the handle is typed as a
 * `ScrollView`, and then does nothing on native.
 *
 * It renders Reanimated's scroll view, which is the library's own default, not
 * React Native's. The screens use `mode="layout"`, where the library reserves
 * the keyboard's space with a spacer view, but its default `insets` mode passes
 * `animatedProps` that only an animated component applies. A plain
 * `ScrollView` there drops them without an error and the respondent types
 * behind the keyboard again, so the animated one stays either way.
 */
export function useSurveyScrollView() {
  const scrollRef = useRef<ScrollView | null>(null);

  /**
   * Stable across renders: a new component identity would remount the scroll
   * view and lose the respondent's position on every keystroke.
   */
  const ScrollViewComponent = useMemo<KeyboardAwareScrollViewComponent>(
    () =>
      forwardRef<Animated.ScrollView, AnimatedScrollViewProps>((props, keyboardAwareRef) => (
        <Animated.ScrollView
          {...props}
          ref={(instance: Animated.ScrollView | null) => {
            scrollRef.current = instance as ScrollView | null;

            if (typeof keyboardAwareRef === 'function') {
              keyboardAwareRef(instance);
            } else if (keyboardAwareRef) {
              keyboardAwareRef.current = instance;
            }
          }}
        />
      )),
    []
  );

  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ y: 0, animated: true });
  }, []);

  return { ScrollViewComponent, scrollToTop };
}
